import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Popover, PopoverContent, PopoverTrigger 
} from "@/components/ui/popover";
import { Bell, Check, Calendar, FileText, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/comms/notifications`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setNotifications(res.data);
    } catch (err) {
      console.error("Failed to fetch notifications");
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/comms/notifications/unread-count`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setUnreadCount(res.data.count);
    } catch (err) {
      console.error("Failed to fetch unread count");
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/comms/notifications/${notificationId}/read`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      fetchNotifications();
      fetchUnreadCount();
    } catch (err) {
      console.error("Failed to mark as read");
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "meeting_invite":
      case "meeting_reminder":
        return Calendar;
      case "minutes_review":
      case "input_deadline":
        return FileText;
      default:
        return AlertTriangle;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "meeting_invite":
        return "text-violet-500 bg-violet-50";
      case "meeting_reminder":
        return "text-blue-500 bg-blue-50";
      case "minutes_review":
        return "text-amber-500 bg-amber-50";
      case "input_deadline":
        return "text-red-500 bg-red-50";
      default:
        return "text-slate-500 bg-slate-50";
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (open) fetchNotifications();
    }}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="border-b border-slate-200 px-4 py-3">
          <h3 className="font-semibold text-slate-800">Notifications</h3>
          <p className="text-xs text-slate-500">{unreadCount} unread</p>
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map((notif) => {
                const Icon = getNotificationIcon(notif.type);
                const colorClass = getNotificationColor(notif.type);
                
                return (
                  <div
                    key={notif.id}
                    className={`p-3 hover:bg-slate-50 cursor-pointer ${
                      !notif.read ? 'bg-violet-50/30' : ''
                    }`}
                    onClick={() => !notif.read && markAsRead(notif.id)}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${notif.read ? 'text-slate-600' : 'font-medium text-slate-800'}`}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {notif.message}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-violet-500 rounded-full flex-shrink-0 mt-2" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
